import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const DISMISS_KEY = 'bragsheet_trial_banner_dismissed';

// Escalating urgency tiers based on trialDaysLeft:
//   > 7        → calm    (light teal, current quiet style)
//   <= 7, > 3  → amber   (warning)
//   <= 3, > 1  → orange  (urgent)
//   <= 1       → red     (critical; last-day variant when === 0)
function getTier(daysLeft) {
  if (daysLeft <= 1) return 'critical';
  if (daysLeft <= 3) return 'urgent';
  if (daysLeft <= 7) return 'warning';
  return 'calm';
}

function getStyle(tier) {
  if (tier === 'critical') {
    return {
      bg: '#fecaca',
      border: '#f87171',
      text: '#991b1b',
      borderWidth: '2px',
      fontWeight: 700,
    };
  }
  if (tier === 'urgent') {
    return {
      bg: '#fed7aa',
      border: '#fb923c',
      text: '#9a3412',
      borderWidth: '2px',
      fontWeight: 600,
    };
  }
  if (tier === 'warning') {
    return {
      bg: '#fef3c7',
      border: '#fcd34d',
      text: '#92400e',
      borderWidth: '1.5px',
      fontWeight: 500,
    };
  }
  // calm
  return {
    bg: '#f0fdfa',
    border: '#99d5cf',
    text: '#01696F',
    borderWidth: '1px',
    fontWeight: 500,
  };
}

function getMessage(tier, daysLeft) {
  const dayWord = `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;

  if (tier === 'critical') {
    // Last day variant when exactly 1 day remains.
    if (daysLeft <= 1) {
      return (
        <>
          <strong>Your trial ends today.</strong> Upgrade now to keep AI Tools, unlimited entries, and PDF reports.
        </>
      );
    }
    return (
      <>
        <strong>Last chance — only {dayWord} left.</strong> Your AI Tools, PDF reports, and unlimited entries end soon.
      </>
    );
  }
  if (tier === 'urgent') {
    return (
      <>
        <strong>Only {dayWord} left</strong> in your Pro trial. Upgrade to keep AI Tools and polished PDF reports.
      </>
    );
  }
  if (tier === 'warning') {
    return (
      <>
        <strong>{dayWord} left</strong> in your Pro trial. Make the most of unlimited entries while you have them.
      </>
    );
  }
  return (
    <>
      You have <strong>{dayWord}</strong> left in your free trial.
    </>
  );
}

export default function TrialBanner({ daysLeft, onDismiss }) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === 'true'
  );

  if (dismissed || daysLeft == null || daysLeft <= 0) return null;

  const tier = getTier(daysLeft);
  const style = getStyle(tier);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  return (
    <div role="status" aria-live="polite" style={{
      backgroundColor: style.bg,
      borderBottom: `${style.borderWidth} solid ${style.border}`,
      padding: '0.6rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      fontSize: '0.875rem',
      color: style.text,
      fontWeight: style.fontWeight,
      flexWrap: 'wrap',
    }}>
      <span>{getMessage(tier, daysLeft)}</span>
      <Link to="/pricing" style={{
        color: style.text,
        fontWeight: '700',
        textDecoration: 'underline',
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
      }}>
        {tier === 'critical' || tier === 'urgent' ? 'Keep Pro' : 'Upgrade'}
      </Link>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss trial banner"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: style.text, fontSize: '1.1rem', padding: '0 0.25rem',
          lineHeight: 1, marginLeft: '0.25rem', opacity: 0.6,
        }}
      >
        ×
      </button>
    </div>
  );
}
